import * as React from "react";
import { useState, useEffect } from "react";
import { useTheme, useColorScheme } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MenuIcon from "@mui/icons-material/Menu";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import MenuContent from "./MenuContent";
import OptionsMenu from "./OptionsMenu";
import { getCurrentTenant } from "../config/tenantConfig";
import { useSidebar } from "../contexts/SidebarContext";

const drawerWidth = 240;

export default function SideMenu() {
  const theme = useTheme();
  const { mode, setMode } = useColorScheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sidebarOpen, toggleSidebar } = useSidebar();

  // Anlık saat ve timezone
  const [currentTime, setCurrentTime] = useState(new Date());
  const timezoneOffset = -(new Date().getTimezoneOffset() / 60);
  const timezoneLabel = `UTC${timezoneOffset >= 0 ? '+' : ''}${timezoneOffset}`;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const toggleMode = () => {
    // Geçici olarak dark/light mode geçişi kapatıldı, sadece light mod
    // if (!mode) return;
    // setMode(mode === "light" ? "dark" : "light");
  };
  const tenant = getCurrentTenant();
  const logoLight = tenant?.theme?.logoLight ?? tenant?.logoLight;
  const logoDark = tenant?.theme?.logoDark ?? tenant?.logoDark;
  const logoSrc =
    (theme.palette.mode === "dark" ? logoDark : logoLight) || null;
  const userName = localStorage.getItem("userName");
  const displayName = userName
    ? userName.split("@")[0]
    : localStorage.getItem("userName");
  const companyName = localStorage.getItem("companyName");
  const rolesRaw = localStorage.getItem("userRoles");
  let roles = [];
  try {
    if (rolesRaw) {
      const parsed = JSON.parse(rolesRaw);
      if (Array.isArray(parsed)) roles = parsed;
      else if (typeof parsed === "string") roles = [parsed];
    }
  } catch (e) {
    // fallback: try to split plain text like "admin,superAdmin" or '["admin","superAdmin"]'
    roles = (rolesRaw || "")
      .replaceAll("[", "")
      .replaceAll("]", "")
      .replaceAll('"', "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const formatRole = (r) =>
    r.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
  // If user has superAdmin among roles, show only superAdmin
  const hasSuperAdmin = roles.some(
    (r) => String(r).toLowerCase() === "superadmin"
  );
  const displayRoles = hasSuperAdmin ? ["superAdmin"] : roles;
  return (
    <>
      {/* Hamburger button - visible only when sidebar is closed, desktop only */}
      <IconButton
        onClick={toggleSidebar}
        sx={{
          position: "fixed",
          top: 16,
          left: 16,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: sidebarOpen ? "none" : { xs: "none", md: "flex" },
          backgroundColor: "background.paper",
          boxShadow: 2,
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <MenuIcon />
      </IconButton>

      <MuiDrawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: sidebarOpen ? drawerWidth : 0,
          flexShrink: 0,
          boxSizing: "border-box",
          transition: "width 0.3s ease",
          [`& .${drawerClasses.paper}`]: {
            width: sidebarOpen ? drawerWidth : 0,
            boxSizing: "border-box",
            backgroundColor: "background.paper",
            overflow: "hidden",
            transition: "width 0.3s ease",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: "calc(var(--template-frame-height, 0px) + 4px)",
            p: 3,
            cursor: "pointer",
          }}
          onClick={toggleSidebar}
        >
          {logoSrc ? (
            <Box
              component="img"
              src={logoSrc}
              alt={`${tenant?.name ?? "App"} Logo`}
              sx={{
                height: 44,
                width: "auto",
                display: "block",
                objectFit: "contain",
                userSelect: "none",
                transition: "opacity 0.2s",
                "&:hover": { opacity: 0.8 },
              }}
            />
          ) : (
            <Typography
              sx={{
                transition: "opacity 0.2s",
                "&:hover": { opacity: 0.8 },
                ...(tenant?.textStyle || {}),
              }}
            >
              {tenant?.name || "App"}
            </Typography>
          )}
        </Box>
        <Divider />
        <Box
          sx={{
            overflow: "auto",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MenuContent />
        </Box>

        <Stack
          direction="row"
          sx={{
            p: 2,
            gap: 1,
            alignItems: "center",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Avatar
            sizes="small"
            alt={displayName}
            // src="/static/images/avatar/7.jpg"
            sx={{ width: 36, height: 36 }}
          />
          <Box sx={{ mr: "auto" }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, lineHeight: "16px" }}
            >
              {displayName}
            </Typography>
          </Box>
          <OptionsMenu />
        </Stack>
        <Stack
          direction="row"
          sx={{
            p: 1.1,
            alignItems: "center",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              divider={<Divider orientation="vertical" flexItem />}
            >
              {companyName && (
                <Typography
                  variant="body3"
                  onClick={() => navigate("/client")}
                  sx={{
                    fontWeight: 500,
                    lineHeight: "16px",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {companyName}
                </Typography>
              )}
              {displayRoles.length > 0 && (
                <Typography
                  variant="body3"
                  sx={{ fontWeight: 500, lineHeight: "16px" }}
                >
                  {displayRoles.map(formatRole).join(", ")}
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>
        <Stack
          direction="row"
          sx={{
            p: 1.1,
            alignItems: "center",
            justifyContent: "center",
            borderTop: "1px solid",
            borderColor: "divider",
            gap: 1,
          }}
        >
          <Tooltip title={timezoneLabel} arrow placement="top">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ cursor: "pointer" }}>
              <AccessTimeIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                {currentTime.toLocaleTimeString()}
              </Typography>
            </Stack>
          </Tooltip>
        </Stack>
      </MuiDrawer>
    </>
  );
}
